<script setup>
import { computed, onMounted, ref } from "vue"
import Swal from "sweetalert2"
import api from "@/api/axios.js"
import OrgNode from "@/components/rrhh/OrgNode.vue"

const empleados = ref([])
const cargando = ref(true)

async function cargar() {
  cargando.value = true
  try {
    const { data } = await api.get("/rrhh/organigrama")
    empleados.value = data.empleados
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el organigrama",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    cargando.value = false
  }
}

onMounted(cargar)

// El jefe inmediato real (managerId) de todos los empleados siempre es RRHH
// (Supervisor queda sin jefe, a la misma altura que RRHH). La jerarquía
// visual Supervisor > Asesor Lider > Cotizador no existe como managerId en
// la base de datos: se arma acá reenganchando esos nodos bajo el cargo que
// les corresponde, sin tocar los datos reales. Dpto Financiero, Asesor
// Corporativo y cualquier otro cargo se quedan donde los dejó managerId
// (rama aparte, colgando directo de RRHH).
const arbol = computed(() => {
  const porId = new Map(empleados.value.map((e) => [e.id, { ...e, children: [] }]))
  const raices = []

  for (const e of porId.values()) {
    if (e.managerId && porId.has(e.managerId)) {
      porId.get(e.managerId).children.push(e)
    } else {
      raices.push(e)
    }
  }

  function sacarDeSuPadre(nodo) {
    for (const posiblePadre of porId.values()) {
      const i = posiblePadre.children.indexOf(nodo)
      if (i !== -1) {
        posiblePadre.children.splice(i, 1)
        return
      }
    }
    const i = raices.indexOf(nodo)
    if (i !== -1) raices.splice(i, 1)
  }

  const porCargo = (nombre) => [...porId.values()].filter((n) => n.cargo === nombre)

  // Solo se reengancha bajo el primer Supervisor/Asesor Lider encontrado
  // (caso normal: uno solo de cada uno). Si no existe ninguno, esos cargos
  // quedan donde managerId los haya puesto.
  const [supervisor] = porCargo("Supervisor")
  if (supervisor) {
    for (const lider of porCargo("Asesor Lider")) {
      sacarDeSuPadre(lider)
      supervisor.children.push(lider)
    }
  }

  const [asesorLider] = porCargo("Asesor Lider")
  if (asesorLider) {
    for (const cotizador of porCargo("Cotizador")) {
      sacarDeSuPadre(cotizador)
      asesorLider.children.push(cotizador)
    }
  }

  return raices
})
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card shadow-sm">
      <div class="card-header">
        <h5 class="mb-0"><i class="ni ni-world-2 text-primary me-2"></i>Organigrama</h5>
      </div>
      <div class="card-body">
        <div v-if="cargando" class="text-center text-muted py-5">
          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
          Cargando organigrama...
        </div>
        <div v-else-if="arbol.length === 0" class="text-center text-muted py-5">
          Aún no hay empleados activos registrados.
        </div>
        <div v-else class="org-chart-scroll">
          <ul class="org-tree">
            <OrgNode v-for="raiz in arbol" :key="raiz.id" :node="raiz" />
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.org-chart-scroll {
  overflow-x: auto;
  padding-bottom: 1rem;
}

.org-tree {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: center;
  min-width: max-content;
}
</style>
